import { Toast as RadixToast } from "radix-ui";
import { Button } from "./Button.tsx";
import { IconButton } from "./IconButton.tsx";

export type ToastProps = {
  open: boolean;
  /** The user dismissed it (close button, swipe, Escape). */
  onClose: () => void;
  message: string;
  /** One action, e.g. "Reload". */
  action?: { label: string; onPress: () => void; testID: string };
  /** Translated label of the close button. */
  closeLabel: string;
  /** Auto-dismiss after this many ms; absent = stays until acted on (never interrupts typing: it takes no focus). */
  durationMs?: number;
  testID?: string;
};

/**
 * Small, non-modal notification at the bottom of the window (web: Radix Toast in a fixed viewport; iOS: glass
 * banner overlay; Android: Snackbar). Never steals focus, so it can't interrupt someone mid-typing.
 */
export function Toast({ open, onClose, message, action, closeLabel, durationMs, testID }: ToastProps) {
  return (
    <RadixToast.Provider swipeDirection="down" duration={durationMs ?? Number.POSITIVE_INFINITY}>
      <RadixToast.Root
        className="b-toast"
        open={open}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        type="background"
        data-testid={testID}
      >
        <RadixToast.Description className="b-toast-message">{message}</RadixToast.Description>
        {action === undefined ? null : (
          <RadixToast.Action asChild altText={action.label}>
            <Button label={action.label} variant="primary" size="sm" onPress={action.onPress} testID={action.testID} />
          </RadixToast.Action>
        )}
        <RadixToast.Close asChild aria-label={closeLabel}>
          <IconButton icon="close" accessibilityLabel={closeLabel} size="sm" onPress={onClose} testID={`${testID ?? "toast"}-close`} />
        </RadixToast.Close>
      </RadixToast.Root>
      <RadixToast.Viewport className="b-toast-viewport" />
    </RadixToast.Provider>
  );
}
